import { useCurrentGuest } from '@carlito/hub-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { carlitoApiQueryClient } from '@carlito/api-client';
import { getPersistedBookingIds } from '../persistence';
import { createBooking } from '../queries/createBooking';
import { createEvent } from '../queries/createEvent';
import { createEventCategory } from '../queries/createEventCategory';
import { deleteEvent } from '../queries/deleteEvent';
import { deleteEventCategory } from '../queries/deleteEventCategory';
import { editBooking } from '../queries/editBooking';
import { EditEventParams, editEvent } from '../queries/editEvent';
import { editEventCategory } from '../queries/editEventCategory';
import { ListAvailableTimeslotsParams, listAvailableSlots } from '../queries/listAvailableSlots';
import { ListBookingsParams, ListBookingsResponse, listBookings } from '../queries/listBookings';
import {
  ListEventCategoriesQueryParameters,
  listEventCategories,
} from '../queries/listEventCategories';
import {
  ConditionallyLocalizedListEventsResponse,
  ListEventsQueryParameters,
  listEvents,
} from '../queries/listEvents';
import { BookingStatusFilterQuery } from '../types';

import { useBookitoSettings } from '@carlito/api';
import { syncSpaData } from '../queries/syncSpaData';
import { reorderEventCategory } from '../queries/reorderEventCategory';

export type { CreateBookingParams } from '../queries/createBooking';
export type { CreateEventParams } from '../queries/createEvent';
export type { CreateEventCategoryParams } from '../queries/createEventCategory';
export type { DeleteEventParams } from '../queries/deleteEvent';
export type { EditBookingParams } from '../queries/editBooking';
export type { EditEventParams } from '../queries/editEvent';
export type { EditEventCategoryParams } from '../queries/editEventCategory';
export type {
  ListAvailableTimeslotsParams,
  ListAvailableTimeslotsResponse,
} from '../queries/listAvailableSlots';
export type { ListBookingsParams, ListBookingsResponse } from '../queries/listBookings';
export type { ListEventCategoriesResponse } from '../queries/listEventCategories';
export type { ListEventsQueryParameters, ListEventsResponse } from '../queries/listEvents';
export { usePmsEventBooking } from './usePmsEventBooking';

export type ListEventsWithFullDataResponse = ConditionallyLocalizedListEventsResponse<false>;
export type FullEventData = ConditionallyLocalizedListEventsResponse<false>['events'][0];
export type LocalizedEventData = ConditionallyLocalizedListEventsResponse<true>['events'][0];

export type EventOccurrences = NonNullable<
  ReturnType<typeof useListLocalizedEvents>['data']
>['events'][0]['occurrences'];

export const queryClient = carlitoApiQueryClient;

export function onLanguageChanged() {
  queryClient.invalidateQueries();
}

export function onJwtChanged() {
  queryClient.invalidateQueries();
}

export function useListEvents<T extends boolean>(
  params: ListEventsQueryParameters<T>,
  enabled = true
) {
  return useQuery({ enabled, queryKey: ['events', params], queryFn: () => listEvents<T>(params) });
}

export type ListEventsWithFullDataQueryParameters = Omit<
  ListEventsQueryParameters,
  'localize' | 'includeHidden' | 'highlighted'
> & {
  includeHidden?: boolean;
  highlighted?: boolean;
};

export function useListEventsWithFullData(
  params: ListEventsWithFullDataQueryParameters,
  enabled = true
) {
  return useQuery({
    queryKey: ['events', params],
    enabled,

    queryFn: () =>
      listEvents({
        ...params,
        localize: false,
        includeHidden: params.includeHidden ? true : undefined,
        highlighted: params.highlighted ? true : undefined,
      }),
  });
}

export type ListEventsWithMultipleFiltersParameters = ListEventsWithFullDataQueryParameters & {
  requestId: string;
};

export type ListEventsWithMultipleFiltersResponse = {
  requestId: string;
} & ListEventsWithFullDataResponse;

export function useListEventsWithMultipleFilters(
  filters: ListEventsWithMultipleFiltersParameters[],
  enabled = true
) {
  return useQuery({
    queryKey: ['events', filters],
    enabled,

    queryFn: async () => {
      const promises = filters.map(async ({ requestId, ...queryFilter }) => {
        const res = await listEvents({
          ...queryFilter,
          localize: false,
        });

        return {
          requestId,
          ...res,
        };
      });

      const data = await Promise.all(promises);
      return data;
    },
  });
}

type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
  [Property in Key]-?: Type[Property];
};
type ListPaginatedLocalizedEventParams = WithRequiredProperty<
  Omit<ListEventsQueryParameters, 'localize' | 'toDate'>,
  'fromDate'
>;

function adjustForTimezone(date: Date): Date {
  const timeOffsetInMS: number = date.getTimezoneOffset() * 60000;
  date.setTime(date.getTime() - timeOffsetInMS);
  return date;
}

export function useListAvailableTimeslots(params: ListAvailableTimeslotsParams, enabled = true) {
  const { isLoading, ...rest } = useQuery({
    queryKey: ['events-time-slots', params],
    enabled,
    queryFn: () => listAvailableSlots(params),
  });

  return { isLoading: enabled && isLoading, ...rest };
}

export function useListPaginatedLocalizedEvents(
  params: ListPaginatedLocalizedEventParams,
  enabled = true
) {
  const bookitoSettings = useBookitoSettings();
  const loadDays = bookitoSettings.bookitoSettings?.daysViewableInAdvance;

  return useInfiniteQuery({
    queryKey: ['events', params],
    enabled: enabled && Boolean(loadDays),

    queryFn: async ({ pageParam }) => {
      if (!loadDays) {
        throw new Error('Bookito settings not loaded');
      }

      const lastFromDate = pageParam || params.fromDate;

      const fromDate = adjustForTimezone(new Date(lastFromDate));

      const toDate = new Date(fromDate.toISOString());
      toDate.setDate(toDate.getDate() + loadDays);

      const response = await listEvents({
        ...params,
        toDate: toDate.toISOString(),
        fromDate: lastFromDate,
        localize: true,
      });

      return {
        ...response,
        lastFromDate,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!loadDays) {
        return undefined;
      }

      if (lastPage.events.length === 0) {
        return undefined;
      }

      const newFromDate = new Date(lastPage.lastFromDate);
      newFromDate.setDate(newFromDate.getDate() + loadDays);

      return newFromDate.toISOString();
    },
  });
}

export function useListLocalizedEvents(
  params: Omit<ListEventsQueryParameters, 'localize'>,
  enabled = true
) {
  return useQuery({
    queryKey: ['events', params],
    enabled,
    queryFn: () =>
      listEvents({
        ...params,
        localize: true,
      }),
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEditEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

type EditEventPartiallyParams = {
  id: string;
  getEventEditData: (event: FullEventData) => Omit<Partial<EditEventParams>, 'id'>;
};

async function editEventPartially({ getEventEditData, id }: EditEventPartiallyParams) {
  const evQueryResult = await listEvents({
    id: id,
    localize: false,
    includeHidden: true,
    excludeCancelled: false,
  });
  const { hotelId: ownerAuthId, ...fetchedEvent } = evQueryResult.events[0];

  const partialEditData = getEventEditData(fetchedEvent);

  const editData = {
    ...fetchedEvent,
    ...partialEditData,
  };
  return editEvent(editData);
}

export function useEditEventPartially() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editEventPartially,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useListBookings(params: ListBookingsParams, enabled = true) {
  const REFETCH_BOOKINGS_INTERVAL = 10000;
  const { limit = 10 } = params;
  const { data, ...rest } = useInfiniteQuery(
    ['bookings', params],
    ({ pageParam = 0 }) => {
      return listBookings({ ...params, skip: pageParam, limit: limit.toString() });
    },
    {
      refetchInterval: REFETCH_BOOKINGS_INTERVAL,
      enabled,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.bookings.length === 0 || lastPage.bookings.length < Number(limit)) {
          return undefined;
        }
        return allPages.length * Number(limit);
      },
    }
  );

  const bookings = useMemo<ListBookingsResponse['bookings']>(() => {
    if (data?.pages) {
      return data.pages.reduce(
        (acc: ListBookingsResponse['bookings'], el: ListBookingsResponse) => {
          return [...acc, ...el.bookings];
        },
        []
      );
    }
    return [];
  }, [data?.pages]);

  return {
    ...rest,
    data:
      data?.pages && data?.pages.length > 0
        ? { ...data.pages[0], bookings: bookings, type: data.pages[0].type }
        : undefined,
  };
}

type Props = {
  statusFilter?: BookingStatusFilterQuery;
  shouldLoadMore?: boolean;
};
export function useListMyBookings(params?: Props) {
  const { statusFilter, shouldLoadMore = false } = params ?? {};
  const [myLegacyBookingIds, setMyLegacyBookingIds] = useState<string[]>([]);

  // handles backwards compatibility:Load persisted bookings from local storage;
  useEffect(() => {
    const { data } = getPersistedBookingIds();
    setMyLegacyBookingIds(data);
  }, []);

  const { guest } = useCurrentGuest();
  const liskBookingQuery = useListBookings(
    { byGuestId: guest?.id, statusFilter, sort: 'desc' },
    Boolean(guest?.id)
  );

  useEffect(() => {
    if (
      shouldLoadMore &&
      liskBookingQuery.hasNextPage &&
      !liskBookingQuery.isLoading &&
      !liskBookingQuery.isError
    ) {
      liskBookingQuery.fetchNextPage();
    }
  }, [shouldLoadMore, liskBookingQuery.hasNextPage, liskBookingQuery]);

  const { data, isLoading, error } = liskBookingQuery;

  // fetch legacy persisted bookings
  const enabled = myLegacyBookingIds.length > 0;
  const { data: legacyBookings, isLoading: isLoadingLegacyBookings } = useQuery(
    ['MyBookings', guest?.id],
    async () => {
      const bookings = await Promise.all(
        myLegacyBookingIds.map((id) => listBookings({ byId: id }))
      );
      return bookings.map((el) => el?.bookings?.[0]);
    },
    { enabled }
  );

  const isLoadingBookings = isLoading || (isLoadingLegacyBookings && enabled);

  const myBookingsList = useMemo(() => {
    if (isLoadingBookings) return [];
    const filteredLegacyBookings = statusFilter
      ? legacyBookings?.filter((el) => el?.status === statusFilter)
      : legacyBookings;
    return [...(data?.bookings ?? []), ...(filteredLegacyBookings ?? [])];
    // return mergedBookings.sort(
    //   (a, b) => new Date(b.bookedAt).getTime() - new Date(a.bookedAt).getTime()
    // );
  }, [data?.bookings, isLoadingBookings, legacyBookings, statusFilter]);

  return {
    myBookingsList,
    statusFilter,
    isLoading: isLoading,
    isFetchingNextPage: liskBookingQuery.isFetchingNextPage,
    error,
    myBookingIds: myLegacyBookingIds,
  };
}

export function useCreateBooking() {
  return useMutation({
    mutationFn: createBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEditBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useListLocalizedEventCategories(
  params: Omit<ListEventCategoriesQueryParameters, 'localize'> = {},
  enabled?: boolean
) {
  return useQuery({
    queryKey: ['event-categories-localized', params],
    queryFn: () =>
      listEventCategories({
        ...params,
        localize: true,
      }),
    enabled,
  });
}

export function useListEventCategories(
  params: Omit<ListEventCategoriesQueryParameters, 'localize'> = {},
  enabled?: boolean
) {
  const queryKey = ['event-categories', params];
  const res = useQuery({
    queryKey,
    queryFn: () =>
      listEventCategories({
        ...params,
        localize: false,
      }),
    enabled,
  });
  return {
    queryKey,
    ...res,
  };
}

export function useCreateEventCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEventCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event-categories'],
      });
    },
  });
}

export function useEditEventCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editEventCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event-categories'],
      });
    },
  });
}

export function useReorderEventCategory() {
  return useMutation({
    mutationFn: reorderEventCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event-categories'],
      });
    },
  });
}
export function useDeleteEventCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEventCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['event-categories'],
      });
    },
  });
}

export { listBookings };

export function useSyncSpaData() {
  const queryClient = useQueryClient();
  return useMutation(() => syncSpaData({}), {
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['event-categories'],
      });
      queryClient.refetchQueries({
        queryKey: ['events'],
      });
    },
  });
}
